import React, { useEffect } from "react";
import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Topbar from "./pages/global/Topbar";
import Sidebar from "./pages/global/Sidebar";
import Dashboard from "./pages/dashboard";
import Templates from "./pages/templates";
import CreateTemplate from "./pages/templates/create";
import { AlertProvider } from "./global/AlertContext";
import EditTemplate from "./pages/templates/edit";
import PrivateRoute from "./components/PrivateRoute";
import Login from "./pages/login";
import NotFound from "./pages/notFound";
import { useDispatch } from "react-redux";
import { getStoredUser } from "./utils/storage";
import { loginSuccess } from "./features/authSlice";
import ViewTemplate from "./pages/templates/view";
import Products from "./pages/products";
import CreateProduct from "./pages/products/create";
import EditProduct from "./pages/products/edit";
import Tickets from "./pages/tickets";
import CreateTicket from "./pages/tickets/create";
import EditTicket from "./pages/tickets/edit";
import ViewTicket from "./pages/tickets/view";
import Lotes from "./pages/lotes";
import CreateLote from "./pages/lotes/create";
import ViewLote from "./pages/lotes/view";
import EditLote from "./pages/lotes/edit";
import Applications from "./pages/applications";
import CreateApplication from "./pages/applications/create";
import EditApplication from "./pages/applications/edit";
import ApplyApplication from "./pages/applications/apply";
import ViewApplication from "./pages/applications/view";
import Forces from "./pages/forces";
import Export from "./pages/export";
import Suppliers from "./pages/suppliers";
import CreateSupplier from "./pages/suppliers/create";
import EditSupplier from "./pages/suppliers/edit";
import Purchases from "./pages/purchases";
import CreatePurchase from "./pages/purchases/create";
import ViewPurchase from "./pages/purchases/view";
import Usages from "./pages/usages";
import ViewUsage from "./pages/usages/view";
import CreateUsage from "./pages/usages/create";

function App() {

  const dispatch = useDispatch();

  useEffect(() => {
    const user = getStoredUser();
    if (user) {
      dispatch(loginSuccess(user));
    }
  }, [dispatch]);

  const [theme, colorMode] = useMode();
  const location = useLocation();
  const isAuthPage = ["/login"].includes(location.pathname);
  const isNotFoundPage = ["/not-found"].includes(location.pathname);
  const shouldShowSidebar = !(isAuthPage || isNotFoundPage);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <AlertProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div className="app">
            {shouldShowSidebar && <Sidebar />}
            <main className="content">
              {shouldShowSidebar && <Topbar />}
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/not-found" element={<NotFound />} />

                {/* Protected Routes */}
                <Route path="/" element={<PrivateRoute element={<Dashboard />} />} />
                
                  {/* Template */}
                  <Route path="/templates" element={<PrivateRoute element={<Templates />} />} />
                  <Route path="/templates/create" element={<PrivateRoute element={<CreateTemplate />} />} />
                  <Route path="/templates/edit/:id" element={<PrivateRoute element={<EditTemplate />} />} />
                  <Route path="/templates/view/:id" element={<PrivateRoute element={<ViewTemplate />} />} />

                  {/* Ticket */}
                  <Route path="/tickets" element={<PrivateRoute element={<Tickets />} />} />
                  <Route path="/tickets/create" element={<PrivateRoute element={<CreateTicket />} />} />
                  <Route path="/tickets/edit/:id" element={<PrivateRoute element={<EditTicket />} />} />
                  <Route path="/tickets/view/:id" element={<PrivateRoute element={<ViewTicket />} />} />
                  
                  {/* Lote */}
                  <Route path="/lotes" element={<PrivateRoute element={<Lotes />} />} />
                  <Route path="/lotes/create" element={<PrivateRoute element={<CreateLote />} />} />
                  <Route path="/lotes/edit/:id" element={<PrivateRoute element={<EditLote />} />} />
                  <Route path="/lotes/view/:id" element={<PrivateRoute element={<ViewLote />} />} />

                  {/* Forces */}
                  <Route path="/forces" element={<PrivateRoute element={<Forces />} />} />

                  {/* Application */}
                  <Route path="/applications" element={<PrivateRoute element={<Applications />} />} />
                  <Route path="/applications/create" element={<PrivateRoute element={<CreateApplication />} />} />
                  <Route path="/applications/edit/:id" element={<PrivateRoute element={<EditApplication />} />} />
                  <Route path="/applications/apply/:id" element={<PrivateRoute element={<ApplyApplication />} />} />
                  <Route path="/applications/view/:id" element={<PrivateRoute element={<ViewApplication />} />} />
                  
                  {/* Supplier */}
                  <Route path="/suppliers" element={<PrivateRoute element={<Suppliers />} />} />
                  <Route path="/suppliers/create" element={<PrivateRoute element={<CreateSupplier />} />} />
                  <Route path="/suppliers/edit/:id" element={<PrivateRoute element={<EditSupplier />} />} />

                  {/* Product */}
                  <Route path="/products" element={<PrivateRoute element={<Products />} />} />
                  <Route path="/products/create" element={<PrivateRoute element={<CreateProduct />} />} />
                  <Route path="/products/edit/:id" element={<PrivateRoute element={<EditProduct />} />} />

                  {/* Purcghase */}
                  <Route path="/purchases" element={<PrivateRoute element={<Purchases />} />} />
                  <Route path="/purchases/create" element={<PrivateRoute element={<CreatePurchase />} />} />
                  <Route path="/purchases/view/:id" element={<PrivateRoute element={<ViewPurchase />} />} />

                  {/* Usage */}
                  <Route path="/usages" element={<PrivateRoute element={<Usages />} />} />
                  <Route path="/usages/create" element={<PrivateRoute element={<CreateUsage />} />} />
                  <Route path="/usages/view/:id" element={<PrivateRoute element={<ViewUsage />} />} />

                  {/* Export */}
                  <Route path="/export" element={<PrivateRoute element={<Export />} />} />

                {/* Catch-all Route */}
                <Route path="*" element={<Navigate to="/not-found" replace />} />
              </Routes>
            </main>
          </div>
        </ThemeProvider>
      </AlertProvider>
    </ColorModeContext.Provider>
  );
}

export default App;